import { DataHandlerDevice, DataHandlerSnapshot, Snapshot, type Device } from "luxedo-data"
import { LuxedoRPC } from "luxedo-rpc"
import type { CalibrationProcessingStatus, PNCStatus } from "luxedo-rpc/src/routes/deviceControl"
import { Toast } from "toaster"
import { writable, type Writable, get } from "svelte/store"
import { Responsive } from "responsive"

const PROGRESS_POLL_DURATION_MS = 2 * 1000

class CalibrationProcessingError extends Error {
	images: Array<string>
	constructor(images?: Array<string>) {
		super("Calibration processing failed.")
		this.images = images
	}
}

export type CalibrationProgress = {
	progress: number // % progress of total operation
	message: string // the heading string of the current step
	description: string // the description string of the current step
	didError?: boolean // true if there is a calibration error (used for rendering red text, not stopping the calibration)
}

type SuccessCallback = (snap: Snapshot, deviceID: number) => void
type FailCallback = (images: Array<string> | undefined, deviceID: number) => void

const WEIGHT = {
	auto_exposure: 0.02,
	PNC: 0.55,
	processing: 0.4,
}

function calculateStepProgress(step: keyof typeof WEIGHT, progress: number) {
	switch (step) {
		case "processing":
			return WEIGHT.auto_exposure + WEIGHT.PNC + WEIGHT.processing * progress
		case "PNC":
			return WEIGHT.auto_exposure + WEIGHT.PNC * progress
		case "auto_exposure":
			return WEIGHT.auto_exposure * progress
	}
}

/**
 * Handles the state of calibration ONLY during the PNC step.
 */
class PNCTranslator {
	devID: number
	pncID: number
	eidosListenerID: string

	// the interval key - used to clear the listener
	statusInterval: number
	// the error timeout - used to ensure certain errors wait to render
	errorTimeout: number
	// the error waiting to be reported via the timeout
	pendingProgress: string

	// Method to update the proper store with translated calibration progress
	store: Writable<CalibrationProgress>

	constructor(devID: number, pncID: number, store: Writable<CalibrationProgress>) {
		this.devID = devID
		this.pncID = pncID
		this.store = store
	}

	/**
	 * Begins the interval to check the pnc status
	 */
	startListening() {
		return new Promise<void>((res, rej) => {
			const finish = (didFail?: boolean) => {
				clearInterval(this.statusInterval)
				if (this.eidosListenerID)
					DataHandlerDevice.get(this.devID)?.removeUpdateListener(this.eidosListenerID)

				if (didFail) rej()
				else res()
			}

			/**
			 * Processes the pnc state, handling each state relevant to this module (will clear interval on done)
			 * @param status
			 */
			const processStatus = (status: PNCStatus) => {
				console.log("processing calibration status", status)

				let waitToReport: boolean = false
				let newProgress: CalibrationProgress

				const progress = get(this.store).progress

				switch (status.state) {
					case "UNKNOWN_ERROR": // wait 15 seconds and inform user
					case "SEARCHING": // wait 15 seconds and inform user
						waitToReport = true
						newProgress = {
							message: "Lost connection with projector... Trying to reconnect...",
							description: `<span class="warn">The connection with your device was lost, but don't worry! If your device is still powered on, it may still be calibrating. Working to reconnect now...</span>`,
							progress,
							didError: true,
						}
						break
					case "LOST": // device has been offline for a while now
						waitToReport = true
						newProgress = {
							message: "Lost connection with projector...",
							description: `The connection with your device was lost. Ensure your device is powered on and connected to the internet then try again`,
							progress,
							didError: true,
						}
						break

					case "WAITING": // basically initializing
						// another compromise
						// occasionally, will receive "WAITING" amidst capturing; should be ok to skip this report, as device eidos updates are being listened to
						if (this.eidosListenerID) return

						newProgress = {
							message: "Initializing...",
							description:
								"The signal was sent to your device to begin calibration... Waiting for calibration to start.",
							progress,
						}
						break

					case "AUTO_EXPOSURE": // camera adjusting
						newProgress = {
							message: "Fine-tuning camera settings...",
							description: `<span class="warn">This step may take up to 10 minutes while your device calculates the best camera settings for your environment.`,
							progress: calculateStepProgress("auto_exposure", 1),
						}
						break

					case "CAPTURING": // capturing as usual
						newProgress = {
							message: `Capturing calibration images (${status.captured} of ${status.total})...`,
							description: `Calibration is the process of shining patterns from a projector in one location, and taking images from a camera in another location, then using mathematical analysis to map the images accurately to the projector.`,
							progress: calculateStepProgress("PNC", status.captured / status.total),
						}

						break
					case "DONE": // completed (fail or success)
						if (status.exit_code === "SUCCESS") {
							newProgress = {
								message: "Capturing complete. Sending images to be processed...",
								description: `The calibration images have been successfully captured and are being sent to the processing server now.`,
								progress: calculateStepProgress("PNC", 1),
							}
							finish(false)
						} else if (status.exit_code === "NO_CAMERA") {
							newProgress = {
								message: "No camera detected...",
								description: `Unable to capturing calibration images as no camera was detected. Ensure the onboard camera is connected correctly and try again.`,
								progress,
								didError: true,
							}
							finish(true)
						} else if (status.exit_code === "CANCELLED") {
							newProgress = {
								message: "Calibration cancelled...",
								description: `This calibration was cancelled. If you did not cancel it, simply try again. If this continues, reach out to customer support.`,
								progress,
								didError: true,
							}
							finish(true)
						} else {
							// should done with no exit_code here
							console.error("No exit_code for DONE PNC...")
							newProgress = {
								message: "Capturing failed to complete...",
								description: `Unable to finish capturing calibration images. Restart your device and try again. If this continues, reach out to customer support.`,
								progress,
								didError: true,
							}
							finish(true)
						}
						break
				}

				// Serialize progress and check if error timeout needs to be cancelled

				const serializedProgress = JSON.stringify(newProgress)
				if (this.errorTimeout) {
					if (this.pendingProgress && serializedProgress !== this.pendingProgress) {
						console.log("Cancelling error timeout")
						clearTimeout(this.errorTimeout)
						this.errorTimeout = undefined
					}
				}

				if (waitToReport) {
					console.warn("[CAL] Waiting to report progress: ", newProgress)
					this.pendingProgress = serializedProgress
					this.errorTimeout = setTimeout(() => {
						this.store.update((ctx) => ({ ...ctx, ...newProgress }))
						this.pendingProgress = undefined
						this.errorTimeout = undefined
					}, 15000)
				} else {
					this.store.update((ctx) => ({ ...ctx, ...newProgress }))
				}
			}

			/**
			 * Checks the pnc status and provides it to processPNCStatus
			 */
			const checkStatus = async () => {
				const status = await this.getPNCStatus()
				processStatus(status)
			}

			this.statusInterval = setInterval(checkStatus, 1000)
		})
	}

	/**
	 * Simply received the PNC status
	 * @returns The PNC Status
	 */
	async getPNCStatus(): Promise<PNCStatus> {
		return await LuxedoRPC.api.deviceControl.devops_get_pnc_status(this.devID, this.pncID)
	}

	/**
	 * Stops the polling interval
	 */
	stopListening() {
		clearInterval(this.statusInterval)
	}
}

class CalibrationProcessTranslator {
	store: Writable<CalibrationProgress>
	deviceId: number

	constructor(deviceId: number, store: Writable<CalibrationProgress>) {
		this.deviceId = deviceId
		this.store = store
	}

	timeout: number
	startListening(): Promise<Snapshot> {
		return new Promise<Snapshot>((res, rej) => {
			const handleFail = async (images?: Array<string>) => {
				rej(images)
			}

			const handleSuccess = async (snapshot: Snapshot) => {
				res(snapshot)
			}

			/**
			 * Pulls the calibration processing status
			 */
			const fetchStatus = async (): Promise<CalibrationProcessingStatus> => {
				const res: CalibrationProcessingStatus =
					await LuxedoRPC.api.deviceControl.devops_get_cal_progress(this.deviceId)
				return res
			}

			/**
			 * Parses the calibration processing status for this device's run
			 * @param status
			 * @returns true if the processing is finished
			 */
			const processStatus = async (status: CalibrationProcessingStatus): Promise<boolean> => {
				const progress = calculateStepProgress("processing", status.progress)

				if (status.isRunning) {
					// progress still running

					if (status.isValid) {
						// processing is done, but snapshot is not captured
						this.store.set({
							progress: progress,
							message: "Capturing snapshot...",
							description:
								"Calibration is almost complete! Now, we'll capture one final image to accurately map the projection space. This image will be transformed using the calibration result to give you a precise view of the projector's perspective.",
						})
					} else {
						// processing images
						this.store.set({
							progress,
							message: "Processing images...",
							description:
								"Calibration is the process of shining patterns from a projector in one location, and taking images from a camera in another location, then using mathematical analysis to map the images accurately to the projector.",
						})
					}

					// still running, so don't return done
					return false
				} else {
					if (status.isValid) {
						// done and succcessful
						await DataHandlerSnapshot.pull([status.snapshotId])
						const snapshot = DataHandlerSnapshot.get(status.snapshotId)

						this.store.set({
							progress: 1,
							message: "Calibration Complete",
							description:
								"This image has been transformed using the calibration algorithm to accurately reflect the view from the projector's perspective, ensuring precise alignment with your projection space.",
							didError: false,
						})

						handleSuccess(snapshot)
					} else {
						// done processing, but not successful
						const failImages = Object.values(status?.images ?? [])
						// handle errorCode
						switch (status.errorCode) {
							case 3:
								this.store.update((ctx) => ({
									...ctx,
									message: "Unable to finish calibrating with captured images...",
									description:
										"Not enough of the projection space was captured by the calibration camera. Please ensure the camera and projector are fully covering the projection space.",
									didError: true,
								}))
								break
							case 4:
								this.store.update((ctx) => ({
									...ctx,
									message: "No projection detected...",
									description:
										"No projection was detected by the calibration camera. Please ensure the camera is facing the projection space and the projector is connected to and receiving the input from your Luxedo device.",
									didError: true,
								}))
								break
							default:
								this.store.update((ctx) => ({
									...ctx,
									message: "Unable to finish processing calibration...",
									description:
										"The images captured during the calibration process do not appear to provide the needed information to finish this calibration. If this continues, contact support.",
									didError: true,
								}))
								break
						}

						// mark as failed, providing failure images
						handleFail(failImages)
					}

					// done processing
					return true
				}
			}

			let i = 0

			/**
			 * Retreives the status and schedules the next poll
			 */
			const pollForStatus = async () => {
				console.log("polling for cal status...", i++)
				const status = await fetchStatus()
				console.log({ status })
				const isDone = await processStatus(status)
				console.log({ isDone })
				if (!isDone) this.timeout = setTimeout(pollForStatus, PROGRESS_POLL_DURATION_MS)
			}

			pollForStatus()
		})
	}

	stopListening() {
		clearTimeout(this.timeout)
	}
}

/**
 * Handles ALL device calibrations - this is the central source of truth for components regarding calibration.
 */
export namespace DeviceCalibrationManager {
	export type ContextType = CalibrationProgress

	const CONTEXT_DEFAULT: ContextType = {
		progress: 0,
		message: "Initializing...",
		description: `Calibration is the process of shining patterns from a projector in one location, and taking images from a camera in another location, then using mathematical analysis to map the images accurately to the projector.`,
		didError: false,
	}

	// Each calibration instance store (key = device ID)
	let stores: { [index: number]: Writable<ContextType> } = {}
	// project and capture listeners
	let pncListeners: { [index: number]: PNCTranslator } = {}
	// progress listeners
	let prgListeners: { [index: number]: CalibrationProcessTranslator } = {}
	// a mapping of device IDs to PNC IDs
	let pncDeviceMap: { [index: number]: number } = {} // [device.id] : pnc.id

	/**
	 * Calls calibrate and returns the store for this calibration instance
	 * @param device The device to calibrate
	 * @param onSuccess Callback when complete
	 * @param onFailure Callback on error
	 * @returns calibration progress store
	 */
	export function startCalibration(
		device: Device,
		onSuccess: SuccessCallback,
		onFailure: FailCallback
	) {
		if (stores[device.id]) delete stores[device.id]

		// Create new calibration store for this instance
		const deviceStore = writable<ContextType>(CONTEXT_DEFAULT)
		stores[device.id!] = deviceStore

		calibrate(device)
			.then((snap) => {
				onSuccess(snap, device.id)
			})
			.catch((error) => {
				onFailure(error.images, device.id)
			})

		return deviceStore
	}

	/**
	 * Checks if a calibration is running, then subscribes to the pnc processing
	 */
	export async function resumeCalibrationIfRunning(
		device: Device,
		onSuccess: SuccessCallback,
		onFailure: FailCallback
	) {
		try {
			const calData: CalibrationProcessingStatus =
				await LuxedoRPC.api.deviceControl.devops_get_cal_progress(device.id)

			console.log({ [device.id]: calData })

			if (!calData.isRunning) return null

			// Create new calibration store for this instance
			const deviceStore = writable<ContextType>(CONTEXT_DEFAULT)
			stores[device.id!] = deviceStore

			const pncId = calData.pncId
			pncDeviceMap[device.id] = pncId

			followCalibration(device, pncId, calData.progress > 0)
				.then((snap) => {
					onSuccess(snap, device.id)
				})
				.catch((error) => {
					onFailure(error.images, device.id)
				})

			return deviceStore
		} catch (e) {
			console.log(
				`Unable to get progress for device ${device.id} (it probably isn't running a calibration right now)...`,
				e
			)
			return null
		}
	}

	/**
	 * Cancels a calibration for the specified device
	 * @param device the device to cancel calibration
	 */
	export async function cancelCalibration(device: Device) {
		await LuxedoRPC.api.deviceControl.devops_cal_cancel(device.id)
		Toast.success("Calibration cancelled.")
		cleanUp(device)
	}

	/**
	 * Triggers a calibration for the provided device, initializing a new store and listener instances (see classes above) to report progress.
	 * @param device The device to calibrate
	 * @returns the resulting snapshot
	 * @throws CalibrationProcessingError w/ images | generic Error
	 */
	async function calibrate(device: Device) {
		// ensure this session is listening to device updates (not sure if this is even necessary as eidos updates are no longer needed)
		LuxedoRPC.addCustomData("maintain_control_of", device.id!)
		await LuxedoRPC.api.deviceControl.device_control_aquire(device.id!, 1)

		// initialize the calibration
		const pncId = (await LuxedoRPC.api.deviceControl.devops_cal_start(device.id)).pnc_id
		pncDeviceMap[device.id] = pncId

		const snapshot = await followCalibration(device, pncId)
		return snapshot
	}

	/**
	 * Process the pnc & calibration data to follow calibration while it is running,
	 * returning a snapshot when finished.
	 * @param device
	 * @param pncId
	 * @returns
	 */
	async function followCalibration(device: Device, pncId: number, skipPnc?: boolean) {
		const deviceStore = stores[device.id]

		try {
			if (!skipPnc) {
				// begin listening to pnc updates
				pncListeners[device.id] = new PNCTranslator(device.id, pncId, deviceStore)
				await pncListeners[device.id].startListening()
			}

			// begin listening to calibration progress updates once pnc is complete
			prgListeners[device.id] = new CalibrationProcessTranslator(device.id, deviceStore)
			const snapshot = await prgListeners[device.id].startListening()

			console.log("DEVICE CAL MANAGER GOT SNAPSHOT", snapshot)

			return snapshot
		} catch (e) {
			console.error("Error while calibrating", e)
			if (e && e instanceof Array) throw new CalibrationProcessingError(e)
			else throw new Error("Unable to complete calibration.")
		}
	}

	/**
	 * Stops all listeners and sets state to default
	 * @param device the device to stop listening to
	 */
	export function cleanUp(device: Device) {
		stores[device?.id]?.set(CONTEXT_DEFAULT)
		pncListeners[device?.id]?.stopListening()
		delete pncListeners[device?.id]
		prgListeners[device?.id]?.stopListening()
		delete prgListeners[device?.id]
	}

	/**
	 * Gets the latest calibration images for the provided device
	 * @param device the device who's pnc images are being requested
	 * @returns an array of image URLs or undefined
	 */
	export async function getCalibrationImages(device: Device) {
		const pncID = pncDeviceMap[device.id]
		try {
			const res = await LuxedoRPC.api.pnc.get_pnc_images(pncID)
			return res
		} catch (e) {
			console.error("An error occurred while getting PNC images", e)
			return []
		}
	}

	async function rerun(device: Device, calibration_id: number) {
		const deviceStore = stores[device.id]

		LuxedoRPC.addCustomData("maintain_control_of", device.id!)
		await LuxedoRPC.api.deviceControl.device_control_aquire(device.id!, 1)

		try {
			const { pnc_id, new_cal_id } = await LuxedoRPC.api.deviceControl.devops_cal_rerun(
				device.id,
				calibration_id
			)

			pncDeviceMap[device.id] = pnc_id

			// begin listening to calibration progress updates once pnc is complete
			prgListeners[device.id] = new CalibrationProcessTranslator(pnc_id, deviceStore)
			const snapshot = await prgListeners[device.id].startListening()

			console.log("DEVICE CAL MANAGER GOT SNAPSHOT", snapshot)

			return snapshot
		} catch (e) {
			console.error("Error while calibrating", e)
			if (e && e instanceof Array) throw new CalibrationProcessingError(e)
			else throw new Error("Unable to complete calibration.")
		}
	}

	export function rerunCalibrationProcessing(
		device: Device,
		calibration_id: number,
		onSuccess: (snap: Snapshot, deviceID: number) => void,
		onFailure: (images: Array<string> | undefined, deviceID: number) => void
	) {
		if (stores[device.id]) delete stores[device.id]

		// Create new calibration store for this instance
		const deviceStore = writable<ContextType>({
			progress: 0,
			message: "Processing images...",
			description: ``,
			didError: false,
		} as ContextType)
		stores[device.id!] = deviceStore

		rerun(device, calibration_id)
			.then((snap) => {
				onSuccess(snap, device.id)
			})
			.catch((error) => {
				onFailure(error.images, device.id)
			})

		return deviceStore
	}
}
